"use client";

import { useId } from "react";
import { X } from "lucide-react";
import { PhoneHandoff } from "@/components/ui/phone-handoff";
import { InstallSteps } from "@/components/ui/tls/install-steps";
import { useIsTouch } from "@/hooks/useIsTouch";
import { DUPLICATE_DOWNLOAD_NOTE } from "@/lib/install-steps";
import { TESLA_FACTS } from "@/lib/tesla-facts";

export interface RowReceiptProps {
  soundId: string;
  title: string;
  onDismiss: () => void;
}

/**
 * What a list row becomes after its download: the install steps, the
 * repeat-download warning and, on a touch device, the way off the phone. It
 * sits directly under the row as the row's own continuation, ruled by a
 * hairline, never boxed (PRODUCT.md rule 1), and it stays until the visitor
 * closes it or downloads from another row.
 *
 * Focus stays on the row's Download button: the visitor is mid-list and the
 * download handler announces the result once through lib/announce.ts, so this
 * mounts no live region of its own. The detail-page receipt moves focus
 * because its Download button unmounts; this row's does not.
 */
export function RowReceipt({ soundId, title, onDismiss }: RowReceiptProps) {
  // False until mounted; the receipt only ever mounts after a tap, so there is
  // no server render to disagree with.
  const isTouch = useIsTouch();
  const leadId = useId();
  const { filename } = TESLA_FACTS.usb;

  return (
    <div
      role="group"
      aria-labelledby={leadId}
      data-testid="row-receipt"
      // col-span-full: in a multi-column parent grid the receipt takes the
      // whole line under its row, the way the active plate does.
      className="col-span-full border-b border-[color:var(--tls-border-subtle)] px-4 py-5 max-sm:px-3"
    >
      <div className="flex items-start justify-between gap-4">
        <p id={leadId} className="m-0 pt-2.5 text-base font-semibold text-[var(--tls-fg-strong)]">
          <code className="font-mono text-[0.9em]">{filename}</code>
          {isTouch
            ? " is saved on this device. A Tesla reads it from a USB drive."
            : " downloaded. Put it on a USB drive."}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Close install steps for ${title}`}
          data-icon-btn
          className="tlsr-focus inline-flex size-11 shrink-0 items-center justify-center text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      <InstallSteps variant="condensed" layout="columns" className="mt-4" />
      <p className="m-0 mt-4 text-sm leading-normal text-[var(--tls-fg-muted)]">{DUPLICATE_DOWNLOAD_NOTE}</p>
      {isTouch && (
        <div className="mt-4">
          <PhoneHandoff soundId={soundId} surface="sound_row" />
        </div>
      )}
    </div>
  );
}
